import { Accordion, Button, Input, Text, Image, Toggle } from '@kubit-ui-web/react-components';
import {
  BREAK_CARD_ID,
  MAX_TIMER_SECONDS,
  MIN_TIMER_SECONDS,
  type Participant,
  type RoomState,
  type Story,
} from '@point-ker/shared';
import { useEffect, useState, type FormEvent } from 'react';

import type { UseRoom } from '@/socket/useRoom';
import { BlockIcon, CheckCircleIcon, ChevronDownIcon, LogoMark } from '@/ui/icons';
import { Card, CardGrid, Columns, Hint, Row, Spread, Stack, PlainImage } from '@/ui/layout';
import { ResultsPanel } from '@/screens/ResultsPanel';
import { SessionHistory } from '@/screens/SessionHistory';

/**
 * The estimation room.
 *
 * Everything rendered here comes from the `RoomState` the server last pushed;
 * the component derives nothing about other people. In particular a card is
 * shown as "voted" from `hasVoted`, never from a local guess, so what you see
 * is what everyone else sees.
 */
export function RoomScreen({ room, state }: { room: UseRoom; state: RoomState }) {
  const revealed = state.phase === 'revealed';
  const voters = state.participants.filter((participant) => !participant.isSpectator);

  return (
    <Stack $gap="1.5rem">
      <RoomHeader state={state} onLeave={room.leaveRoom} />

      {room.error !== null && (
        <Card>
          <Spread>
            <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p" color="#c00">
              {room.error.message}
            </Text>
            <Button variant="SECONDARY" size="SMALL" onClick={room.dismissError}>
              Dismiss
            </Button>
          </Spread>
        </Card>
      )}

      <Columns>
        <Stack $gap="1.5rem">
          <StoryPanel room={room} state={state} />

          {state.story === null ? (
            <Card>
              <Text variant="PARAGRAPH_MEDIUM_EXPANDED" component="p">
                {room.isHost
                  ? 'Add a story above to start a round.'
                  : 'Waiting for the host to pick the next story.'}
              </Text>
            </Card>
          ) : (
            <VotingDeck room={room} state={state} />
          )}

          {revealed && state.results !== null && (
            <ResultsPanel
              deck={state.deck}
              results={state.results}
              participants={state.participants}
              votes={state.votes}
              isHost={room.isHost}
              onCommit={room.commitEstimate}
            />
          )}
        </Stack>

        <Stack $gap="1.5rem">
          <ParticipantList
            participants={state.participants}
            votedIds={new Set(state.votes.map((vote) => vote.participantId))}
            hostName={state.hostName}
            selfId={room.participantId}
            votersCount={voters.length}
          />
          <SessionHistory state={state} isHost={room.isHost} />
        </Stack>
      </Columns>
    </Stack>
  );
}

function RoomHeader({ state, onLeave }: { state: RoomState; onLeave: () => void }) {
  const [copied, setCopied] = useState(false);

  const copyInvite = () => {
    const url = `${window.location.origin}/?room=${state.id}`;
    // Clipboard access can be denied or unavailable over plain http; the code
    // is on screen either way, so a failure just means no confirmation.
    void navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <Card>
      <Spread $wrap>
        <Stack $gap="0.15rem">
          <Row>
            <PlainImage>
              <Image
                images={{ DEFAULT: { src: '/logo.svg' } }}
                alt="point-ker"
                width={64}
                height={64}
                loading="eager"
              />
            </PlainImage>
            <Text variant="MAIN_HEADING_H2_EXPANDED" component="h1">
              {state.name}
            </Text>
          </Row>

          <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p">
            {`Room ${state.id} · ${state.deck.name} · hosted by ${state.hostName}`}
          </Text>
        </Stack>
        <Row $wrap>
          <Button variant="SECONDARY" size="SMALL" onClick={copyInvite}>
            {copied ? 'Link copied' : 'Copy invite link'}
          </Button>
          <Button variant="SECONDARY_ALT" size="SMALL" onClick={onLeave}>
            Leave
          </Button>
        </Row>
      </Spread>
    </Card>
  );
}

/**
 * Kubit's accordion is styled for a page section: a solid white panel with a
 * pink spine down the left, a pink chip behind the chevron and a 1.5rem title.
 * All of that would fight the card it sits in here, where the accordion is a
 * disclosure control and not the furniture — so the decoration is turned off
 * and the header is left to size itself from the story block inside it.
 */
const STORY_ACCORDION_STYLES = {
  container: { background_color: 'transparent' },
  decorative: { background_color: 'transparent', width: '0rem' },
  triggerIconContainer: { background_color: 'transparent', padding: '0rem' },
  titleContainer: { margin_left: '0.75rem', gap: '0.5rem' },
  title: { font_size: 'inherit', font_weight: 'inherit', line_height: 'inherit' },
  content: { padding: '1rem 0rem 0rem 0rem' },
};

function StoryPanel({ room, state }: { room: UseRoom; state: RoomState }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [timerOn, setTimerOn] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState('60');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (title.trim() === '') return;
    // Only send a duration when the timer is on and parses to a real number;
    // the server clamps it to range and ignores anything else.
    const parsed = Number(timerSeconds);
    const seconds = timerOn && Number.isFinite(parsed) ? parsed : undefined;
    room.setStory(title.trim(), description.trim() || undefined, seconds);
    setTitle('');
    setDescription('');
  };

  if (!room.isHost) {
    return (
      <Card>
        <CurrentStory story={state.story} />
      </Card>
    );
  }

  return (
    <Card>
      {/* Closed, the host sees exactly what everyone else sees; the form is
          what the panel hides. Keyed on the story so a fresh round opens the
          form again without the host reaching for the chevron — `defaultOpen`
          is read on mount only, this being the uncontrolled accordion. */}
      <Accordion
        key={state.story?.id ?? 'no-story'}
        variant="DEFAULT"
        defaultOpen={state.story === null}
        hasHeaderLineSeparator={false}
        ctv={STORY_ACCORDION_STYLES}
        triggerComponent="div"
        triggerIcon={{ icon: <ChevronDownIcon /> }}
        title={{ content: <CurrentStory story={state.story} />, component: 'div' }}
      >
        <form onSubmit={submit}>
          <Stack>
            <Input
              variant="DEFAULT"
              id="story-title"
              label={{ content: state.story === null ? 'Story to estimate' : 'Next story' }}
              placeholder="As a user I can reset my password"
              value={title}
              maxLength={120}
              onChange={(event) => setTitle(event.target.value)}
            />
            <Input
              variant="DEFAULT"
              id="story-description"
              label={{ content: 'Notes (optional)' }}
              value={description}
              maxLength={1000}
              onChange={(event) => setDescription(event.target.value)}
            />

            {/* Optional round timer: when on, the server auto-reveals once it
                elapses and fills in anyone who did not vote with the lowest
                card. The seconds field stays put and just disables while off,
                so the control never jumps around as it is toggled. */}
            <Row $height="5rem" $gap="1.5rem">
              <Row $gap="0.5rem" $width="fit-content">
                <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
                  Timer:
                </Text>
                <Toggle
                  variant="DEFAULT"
                  inputValues={{ leftInputValue: 'off', rightInputValue: 'on' }}
                  togglePosition={timerOn ? 'RIGHT' : 'LEFT'}
                  onChange={(position) => setTimerOn(position === 'RIGHT')}
                  onIcon={{ icon: <CheckCircleIcon /> }}
                  offIcon={{ icon: <BlockIcon /> }}
                  screenReaderId="story-timer-toggle"
                />
              </Row>
              {timerOn && (
                <Row $width="100%">
                  <Input
                    variant="DEFAULT"
                    id="story-timer"
                    type="number"
                    value={timerSeconds}
                    disabled={!timerOn}
                    onChange={(event) => setTimerSeconds(event.target.value)}
                  />
                  <Hint>{`Seconds per round (${MIN_TIMER_SECONDS}–${MAX_TIMER_SECONDS})`}</Hint>
                </Row>
              )}
            </Row>

            <Row>
              <Button variant="PRIMARY" size="MEDIUM" type="submit" disabled={title.trim() === ''}>
                {state.story === null ? 'Start round' : 'Replace story'}
              </Button>
            </Row>
          </Stack>
        </form>
      </Accordion>
    </Card>
  );
}

/**
 * The story on the table, as everyone in the room sees it. The host gets the
 * same block in the closed accordion above, so collapsing the form leaves the
 * two views identical rather than merely similar.
 */
function CurrentStory({ story }: { story: Story | null }) {
  return (
    <Stack $gap="0.5rem">
      <Text variant="PARAGRAPH_CAPTION_EXPANDED" component="span">
        Current story
      </Text>
      <Text variant="HEADING_H3_EXPANDED" component="h2">
        {story?.title ?? 'None yet'}
      </Text>
      {story?.description != null && (
        <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p">
          {story.description}
        </Text>
      )}
    </Stack>
  );
}

function VotingDeck({ room, state }: { room: UseRoom; state: RoomState }) {
  const revealed = state.phase === 'revealed';
  const self = state.participants.find((participant) => participant.id === room.participantId);
  const voters = state.participants.filter((participant) => !participant.isSpectator);
  const allVoted = voters.length > 0 && state.votes.length >= voters.length;

  return (
    <Card>
      <Spread $wrap>
        <Text variant="HEADING_H3_EXPANDED" component="h2">
          {revealed ? 'Votes are in' : 'Pick a card'}
        </Text>
        <Row $wrap>
          {!revealed && state.voteDeadline !== null && (
            <Countdown deadline={state.voteDeadline} />
          )}
          {/* One interpolation, not three: React splits adjacent expressions
              into separate text nodes, which litters the markup with comment
              separators and makes the copy awkward to read back. */}
          <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
            {`${state.votes.length} of ${voters.length} voted`}
          </Text>
        </Row>
      </Spread>

      {self?.isSpectator === true ? (
        <Text variant="PARAGRAPH_MEDIUM_EXPANDED" component="p">
          You are watching this round as a spectator.
        </Text>
      ) : (
        <CardGrid>
          {state.deck.cards.map((card) => (
            <Button
              key={card.id}
              variant={room.myCardId === card.id ? 'PRIMARY' : 'SECONDARY'}
              size="LARGE"
              disabled={revealed}
              onClick={() => room.castVote(card.id)}
            >
              {/* The break card wears the site's mark: the deck's own cup,
                  rather than whatever ☕ happens to look like on the voter's
                  platform. The label stays ☕ on the wire, which is what the
                  stats, the history and the chart axis read. */}
              {card.id === BREAK_CARD_ID ? <LogoMark /> : card.label}
            </Button>
          ))}
        </CardGrid>
      )}

      {room.isHost && (
        <>
          <Row $wrap>
            <Button
              variant="PRIMARY"
              size="MEDIUM"
              disabled={revealed || state.votes.length === 0}
              onClick={room.revealVotes}
            >
              Reveal votes
            </Button>
            <Button variant="SECONDARY" size="MEDIUM" onClick={room.resetVotes}>
              Reset round
            </Button>
            {/* Running the round and estimating in it are separate jobs, and
                which one the host is doing can change between stories. Locked
                while the votes are up: the server refuses a switch that would
                rewrite results the room is already discussing. */}
            <Row $gap="0.5rem" $width="fit-content">
              <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
                I am voting:
              </Text>
              <Toggle
                variant="DEFAULT"
                inputValues={{ leftInputValue: 'watching', rightInputValue: 'voting' }}
                togglePosition={self?.isSpectator === true ? 'LEFT' : 'RIGHT'}
                onChange={(position) => room.setSpectating(position === 'LEFT')}
                onIcon={{ icon: <CheckCircleIcon /> }}
                offIcon={{ icon: <BlockIcon /> }}
                disabled={revealed}
                screenReaderId="host-voting-toggle"
              />
            </Row>
            {!revealed && allVoted && (
              <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
                Everyone has voted.
              </Text>
            )}
          </Row>
        </>
      )}
    </Card>
  );
}

/**
 * Live countdown to the round's auto-reveal.
 *
 * Purely cosmetic: the server owns the deadline and reveals the votes when it
 * fires, so a client whose clock drifts or whose tab was backgrounded shows a
 * slightly-off number at worst, never a wrong outcome. `deadline` is epoch ms.
 */
function Countdown({ deadline }: { deadline: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [deadline]);

  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));
  return (
    <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span" color={remaining <= 5 ? '#c0362c' : undefined}>
      {remaining > 0 ? `Reveals in ${remaining}s` : 'Revealing…'}
    </Text>
  );
}

function ParticipantList({
  participants,
  votedIds,
  hostName,
  selfId,
  votersCount,
}: {
  participants: readonly Participant[];
  votedIds: ReadonlySet<string>;
  hostName: string;
  selfId: string | null;
  votersCount: number;
}) {
  return (
    <Card>
      <Text variant="HEADING_H3_EXPANDED" component="h2">
        In the room ({votersCount})
      </Text>
      <Stack $gap="0.5rem">
        {participants.map((participant) => (
          <Spread key={participant.id}>
            <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
              {[
                participant.name,
                participant.id === selfId ? '(you)' : null,
                participant.name === hostName ? 'host' : null,
                participant.isSpectator ? 'watching' : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
            <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
              {!participant.connected
                ? 'away'
                : participant.isSpectator
                  ? '—'
                  : votedIds.has(participant.id)
                    ? 'voted'
                    : 'thinking'}
            </Text>
          </Spread>
        ))}
      </Stack>
    </Card>
  );
}

